import type { Locale, Notification } from "./types"
import { v4 as uuidv4 } from "uuid"
import { DEFAULT_LOCALE } from "@/lib/config"
import { appsignal } from "./appsignal"
import type { Span } from "@appsignal/javascript/dist/cjs/span"
import { errorTranslations } from "../i18n/errorTranslations"

export enum AppErrors {
  ApiClient = "ApiClient",
  ApiClientRequest = "ApiClientRequest",
  ApiClientUnauthorized = "ApiClientUnauthorized",
  ValidateUser = "ValidateUser",
  ValidateUserRequest = "ValidateUserRequest",
  ValidateUserResponse = "ValidateUserResponse",
  ValidateUserStoreEmpty = "ValidateUserStoreEmpty",
  CreateUser = "CreateUser",
  CreateUserRequest = "CreateUserRequest",
  CreateUserResponse = "CreateUserResponse",
  CreateActivity = "CreateActivity",
  CreateActivityRequest = "CreateActivityRequest",
  CreateActivityResponse = "CreateActivityResponse",
  FetchJourney = "FetchJourney",
  FetchJourneyRequest = "FetchJourneyRequest",
  FetchJourneyResponse = "FetchJourneyResponse",
  FetchContentVersion = "FetchContentVersion",
  FetchContentVersionRequest = "FetchContentVersionRequest",
  FetchContentVersionResponse = "FetchContentVersionResponse",
  UseContentVersion = "UseContentVersion",
  UseContentVersionEmpty = "UseContentVersionEmpty",
  UseContentVersionInvalid = "UseContentVersionInvalid"
}

export class AppError extends Error {
  data: string | Record<string, unknown>

  constructor(
    errorCode: AppErrors | string,
    data: string | Record<string, unknown> = {}
  ) {
    super()
    this.name = "App." + errorCode
    this.message = "App." + errorCode
    this.data = data
  }

  public getHumanReadableText(locale: Locale): string {
    return (
      errorTranslations[locale]?.[this.name.split(".")[1] as AppErrors] ||
      errorTranslations.en?.[this.name.split(".")[1] as AppErrors] ||
      "Unknown Error"
    )
  }
}

type HashMap<T> = { [key: string]: T }

const sendError = (
  error: AppError,
  errorName: string,
  errorTags: HashMap<unknown> = {}
) => {
  const stringifiedTags: HashMap<string> = Object.entries(errorTags).reduce(
    (acc, [key, value]) => {
      acc[key] = String(value)
      return acc
    },
    {} as HashMap<string>
  )

  const getCurrentJourneyUserId = (): string => {
    const userStore = useUserStore()
    return userStore?.id || ""
  }

  stringifiedTags.currentJourneyUserId = getCurrentJourneyUserId()
  if (appsignal) {
    appsignal.sendError(error, (span: Span) => {
      span.setAction(errorName)
      span.setTags(stringifiedTags)
    })
  }
}

const displayError = (error: AppError | Error) => {
  const id = uuidv4()
  const I18n = useI18nStore()
  const message =
    error instanceof AppError
      ? error.getHumanReadableText((I18n?.locale as Locale) || DEFAULT_LOCALE)
      : error?.message || "Unknown"
  const notification: Notification = {
    id,
    message,
    timestamp: new Date(),
    type: "error"
  }

  const notificationsStore = useNotificationsStore()
  notificationsStore.addNotification(notification)
}

export const handleAppError = (error: unknown, fallbackError: AppError) => {
  const appError = error instanceof AppError ? error : fallbackError
  const errorName = appError ? appError.name : "Unknown"

  console.error(
    appError
      ? `An AppError occurred ${fallbackError ? "(fallback)" : ""}`
      : "An unknown error occurred",
    { error: appError }
  )

  sendError(appError, errorName)
  displayError(appError)
}
